// Every media event, live or replayed, waits in a 150ms debounce where the latest
// event for each mediaId wins, so a replayed burst collapses to each item's final
// state through the same path as live play.
//
// Only hosts play audio: on playing routes every media event is dropped, so
// players' devices stay silent. Browsers allow audio only after a user gesture,
// so Web Audio starts on the first click, touch, or key press, and hooks that
// need audio wait for webAudioReady instead of failing on a null service.

import React, {
  createContext,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useState,
} from 'react';
// Note: Import hooks directly (not from "../hooks") to avoid cycles.
import { useMediaSpeechManager } from '../hooks/useMediaSpeechManager';
import { useMediaSfxManager } from '../hooks/useMediaSfxManager';
import { useMediaMusicManager } from '../hooks/useMediaMusicManager';
import { useMediaImageManager } from '../hooks/useMediaImageManager';
import { useMediaVolumeManager } from '../hooks/useMediaVolumeManager';
import { useMediaInitialization } from '../hooks/useMediaInitialization';
import { useRoute } from '../hooks/useRoute';
import { useRoomInteractionWindow } from '../hooks/useRoomInteractionWindow';
import { useRoomState } from '../hooks/useRoomState';
import { useRoomConfig } from '../hooks/useRoomConfig';
import { useRoomSocket } from '../hooks/useRoomSocket';
import { useAuth } from '../hooks/useAuth';
import { WebAudioService } from '../services/audio/webAudioService';
import {
  Song,
  MediaType,
  MediaEvent,
  MediaReadyEvent,
  MediaStartedEvent,
} from '@airhead-games/shared/types';
import {
  createLogger,
  type SongMetadata,
} from '@airhead-games/shared/utilities';
const logger = createLogger('MediaContext');

// Creates media managers and provides unified API to components
export interface MediaContextValue {
  // Speech domain
  isSpeaking: boolean;
  speechMediaIdPlaying: string | null;
  stopSpeechPlayback: () => void;
  skipCurrentSpeech: () => void;
  playSpeechBuffer: (buffer: Uint8Array) => Promise<void>;
  waitForSpeech: () => Promise<void>;
  waitForSpeechById: (mediaId: string) => Promise<void>;

  // SFX domain
  isSfxPlaying: boolean;
  requestSoundEffect: (sfxId: string) => Promise<string>;
  requestGameSfx: (gameId: string) => Promise<void>;
  playSoundEffect: (sfxId: string) => Promise<void>;
  playNote: (semitones: number) => void;
  stopSfxPlayback: () => void;

  // Music domain
  isMusicPlaying: boolean;
  hasMusicPlaylist: boolean;
  musicTrackCurrent: Song | null;
  musicTrackCurrentMetadata: SongMetadata | null;
  shouldShowMusicDisplay: boolean;
  currentDisplayTrackId: string | null;
  createMusicPlaylist: (
    criteria: Partial<SongMetadata>,
    trackKeys?: string[]
  ) => Promise<number>;
  playMusicPlaylist: () => Promise<void>;
  flushMusicManager: () => void;
  updateTrackWithSignedUrl: (event: MediaReadyEvent) => void;

  // Volume domain (includes mute management)
  isMuted: boolean;
  setIsMuted: React.Dispatch<React.SetStateAction<boolean>>;
  toggleMute: () => void;
  duckMusic: () => void;
  unduckMusic: () => void;
  updateVolumeSettings: (
    volumes: Partial<{
      musicVolume: number;
      musicVolumeDucked: number;
      sfxVolume: number;
      speechVolume: number;
    }>
  ) => void;
  currentVolumes: {
    musicVolume: number;
    musicVolumeDucked: number;
    sfxVolume: number;
    speechVolume: number;
  };

  // Image domain
  loadImageFromEvent: (event: MediaReadyEvent) => Promise<void>;
  loadImageFromUrl: (imageUrl: string) => Promise<void>;
  requestImageScene: (sceneId: string) => Promise<string>;
  requestGameScene: (gameId: string) => Promise<void>;
  getImageSceneItem: (sceneId: string) => string | null;

  // WebAudio service and context control
  webAudio: WebAudioService | null;
  webAudioReady: boolean;
  resumeAudioContext: () => Promise<void>;

  // Audio element refs
  speechAudioRef: React.RefObject<HTMLAudioElement | null>;
  sfxAudioRef: React.RefObject<HTMLAudioElement | null>;
  musicAudioRef: React.RefObject<HTMLAudioElement | null>;
}

export const MediaContext = createContext<MediaContextValue | undefined>(
  undefined
);

interface MediaProviderProps {
  children: React.ReactNode;
  initialMuted?: boolean;
}

export function MediaProvider({
  children,
  initialMuted = true,
}: MediaProviderProps) {
  const { isHostRoute } = useRoute();
  const { registerCleanupCallback } = useRoomSocket();
  const {
    registerMediaEventHandler,
    registerRoomClosedHandler,
    isReplayingEvents,
  } = useRoomState();
  const { roomCode } = useRoomConfig();
  const { user } = useAuth();

  // Create external refs for the audio elements
  const speechAudioRef = useRef<HTMLAudioElement>(null);
  const sfxAudioRef = useRef<HTMLAudioElement>(null);
  const musicAudioRef = useRef<HTMLAudioElement>(null);
  const webAudioRef = useRef<WebAudioService | null>(null);

  // WebAudio readiness state for reactive components
  const [webAudioReady, setWebAudioReady] = useState(false);

  // Global media event debouncing state
  const tempMediaElements = useRef<Map<string, MediaEvent>>(new Map());
  const globalMediaDebouncer = useRef<NodeJS.Timeout | null>(null);

  // Room state for window status monitoring
  const { windowStatus } = useRoomInteractionWindow();
  const prevWindowStatusRef = useRef<string | undefined>(undefined);

  // Audio element setup
  useEffect(() => {
    // Critical fix: Set crossOrigin attribute on all audio elements
    if (speechAudioRef.current) {
      speechAudioRef.current.crossOrigin = 'anonymous';
    }
    if (sfxAudioRef.current) {
      sfxAudioRef.current.crossOrigin = 'anonymous';
    }
    if (musicAudioRef.current) {
      musicAudioRef.current.crossOrigin = 'anonymous';
    }
  }, []);

  // Domain managers creation
  const speechManager = useMediaSpeechManager(speechAudioRef, webAudioReady);
  const volumeManager = useMediaVolumeManager(
    musicAudioRef,
    speechAudioRef,
    sfxAudioRef,
    speechManager.isSpeaking,
    { isMuted: initialMuted }, // initial mute settings
    webAudioRef.current || undefined // pass WebAudio service (convert null to undefined)
  );
  const sfxManager = useMediaSfxManager(
    sfxAudioRef,
    volumeManager.currentVolumes,
    webAudioRef.current || undefined
  );
  const musicManager = useMediaMusicManager(musicAudioRef);
  const imageManager = useMediaImageManager();

  // Track current mute state without causing resumeAudioContext to change
  const currentMuteRef = useRef(volumeManager.isMuted);

  // Keep mute ref synchronized with current mute state
  useEffect(() => {
    currentMuteRef.current = volumeManager.isMuted;
  }, [volumeManager.isMuted]);

  // Seed the session mix from the host's saved defaults on room entry. Auth and
  // room state resolve in either order, so wait for both and seed once per room
  // — re-seeding on a user change would stomp an in-flight in-room save.
  // Deliberately not app-wide: /discover/music shares the music gain node and
  // should keep playing at the default level.
  const seededRoomCodeRef = useRef<string | null>(null);
  const { updateVolumeSettings } = volumeManager;
  useEffect(() => {
    const savedLevels = user?.modSettings?.audioLevels;
    if (!roomCode || !savedLevels || seededRoomCodeRef.current === roomCode) {
      return;
    }
    seededRoomCodeRef.current = roomCode;
    updateVolumeSettings(savedLevels);
  }, [roomCode, user, updateVolumeSettings]);

  // Initialize media based for room/game routes
  useMediaInitialization({
    createMusicPlaylist: musicManager.createMusicPlaylist,
    requestGameSfx: sfxManager.requestGameSfx,
    requestGameScene: imageManager.requestGameScene,
  });

  // Release all temporary media elements after debounce period
  const releaseAllTempMedia = useCallback(() => {
    const elementCount = tempMediaElements.current.size;
    if (elementCount === 0) return;

    // Collect media types for logging
    const mediaTypes = Array.from(tempMediaElements.current.values())
      .map((event) => event.mediaType)
      .filter(Boolean)
      .join(', ');

    logger.debug(
      `Releasing ${elementCount} media elements after debounce... (includes: ${mediaTypes})`
    );

    // Sort by mediaId for consistent processing order
    const sortedMediaIds = Array.from(tempMediaElements.current.keys()).sort();

    sortedMediaIds.forEach((mediaId) => {
      const event = tempMediaElements.current.get(mediaId);
      if (!event) return;

      // Only process media events on room/game routes to prevent expensive operations elsewhere
      if (isHostRoute) {
        // Image events
        if (
          event.type === 'MediaReady' &&
          (event.mediaType === MediaType.IMAGE ||
            event.mediaType === MediaType.BACKGROUND ||
            event.mediaType === MediaType.MUSIC) &&
          event.imageUrl
        ) {
          imageManager.loadImageFromEvent(event as MediaReadyEvent);
        }

        // Speech events
        if (
          (event.type === 'MediaReady' || event.type === 'MediaStarted') &&
          event.mediaType === MediaType.SPEECH &&
          event.speechBuffer
        ) {
          // Drop waitingOnPlayers warnings if the window already closed to avoid stale commentary
          if (
            event.content === 'player.waitingOnPlayers' &&
            windowStatus !== 'opened'
          ) {
            logger.debug(
              'Skipping waitingOnPlayers speech because interaction window is not open'
            );
            return;
          }

          speechManager.queueSpeech(
            event as MediaReadyEvent | MediaStartedEvent
          );
        }

        // Music events
        if (
          event.type === 'MediaReady' &&
          event.mediaType === MediaType.MUSIC &&
          event.musicUrl
        ) {
          musicManager.updateTrackWithSignedUrl(event as MediaReadyEvent);
        }

        // SFX events
        if (
          event.type === 'MediaReady' &&
          event.mediaType === MediaType.SFX &&
          event.sfxUrl &&
          event.content
        ) {
          sfxManager.updateSfxWithSignedUrl(event);
        }
      }
    });

    // Clear temp state
    tempMediaElements.current.clear();
    globalMediaDebouncer.current = null;
  }, [
    imageManager,
    speechManager,
    musicManager,
    sfxManager,
    isHostRoute,
    windowStatus,
  ]);

  // Process media event with global debouncing (gate+release mechanism)
  const processMediaEventWithDebouncing = useCallback(
    (event: MediaEvent) => {
      const { mediaId } = event;

      // Update temporary state with latest event (latest wins for deduplication)
      tempMediaElements.current.set(mediaId, event);

      // Reset global debouncer (50ms gate)
      if (globalMediaDebouncer.current) {
        clearTimeout(globalMediaDebouncer.current);
      }

      globalMediaDebouncer.current = setTimeout(() => {
        releaseAllTempMedia();
      }, 150); // 150ms debounce for safety
    },
    [releaseAllTempMedia]
  );

  // Shared media cleanup function for both room close and navigation cleanup
  const createMediaCleanupFunction = useCallback(() => {
    return () => {
      logger.debug('Flushing all media managers...');

      // Clear global media debouncer
      if (globalMediaDebouncer.current) {
        clearTimeout(globalMediaDebouncer.current);
        globalMediaDebouncer.current = null;
      }
      tempMediaElements.current.clear();

      speechManager.flushSpeechManager();
      sfxManager.flushSfxManager();
      musicManager.flushMusicManager();
      imageManager.flushImageManager();
      logger.debug('Media manager flush completed');
    };
  }, [speechManager, sfxManager, musicManager, imageManager]);

  // Central media event handler with global debouncing
  useEffect(() => {
    registerMediaEventHandler((event) => {
      // Handle all media events
      if (
        event.type !== 'MediaRequested' &&
        event.type !== 'MediaReady' &&
        event.type !== 'MediaStarted' &&
        event.type !== 'MediaCompleted'
      ) {
        return;
      }

      const mediaEvent = event as MediaEvent;

      // Route ALL media events through debouncing gate
      processMediaEventWithDebouncing(mediaEvent);
    });

    // No cleanup needed - registration is replaced on next render
  }, [registerMediaEventHandler, processMediaEventWithDebouncing]);

  // Room closed event handler - cleanup audio when room closes
  useEffect(() => {
    const flushAllMediaManagers = createMediaCleanupFunction();

    registerRoomClosedHandler(() => {
      logger.debug('Room closed: Flushing all media managers...');
      flushAllMediaManagers();
    });
  }, [registerRoomClosedHandler, createMediaCleanupFunction]);

  // Navigation cleanup handler - cleanup media when navigating away from rooms
  useEffect(() => {
    const flushAllMediaManagers = createMediaCleanupFunction();

    // Register for navigation cleanup (room mismatch scenarios)
    return registerCleanupCallback(() => {
      logger.debug('Room mismatch: Flushing all media managers...');
      flushAllMediaManagers();
    });
  }, [registerCleanupCallback, createMediaCleanupFunction]);

  // WebAudio service management initializes for every route
  const resumeAudioContext = useCallback(async (): Promise<void> => {
    if (!webAudioRef.current) {
      const service = new WebAudioService();
      webAudioRef.current = service;

      // Connect sources now that the service exists
      if (speechAudioRef.current)
        service.connectSource(speechAudioRef.current, 'speech');
      if (sfxAudioRef.current)
        service.connectSource(sfxAudioRef.current, 'sfx');
      if (musicAudioRef.current)
        service.connectSource(musicAudioRef.current, 'music');

      // Apply initial mute state using ref to avoid callback dependency
      service.setMute(currentMuteRef.current);

      // Notify components that WebAudio is now available
      setWebAudioReady(true);
    }
    await webAudioRef.current.resume();
  }, []); // Stable callback - no dependencies

  // Effects and event handlers
  // Apply volume manager mute state to WebAudio service
  useEffect(() => {
    if (webAudioRef.current) {
      logger.info(`Setting global mute to ${volumeManager.isMuted}`);
      webAudioRef.current.setMute(volumeManager.isMuted);
    }
  }, [volumeManager.isMuted, webAudioReady]); // Run when mute changes OR service is created

  // Handle user gestures to resume audio context
  useEffect(() => {
    const resumeAudio = async () => {
      // Skip audio context resume during event replay to prevent audio issues
      if (isReplayingEvents) {
        logger.debug('Skipping audio context resume during event replay');
        return;
      }

      await resumeAudioContext();
      // Keep listeners active - iOS can suspend audio context during navigation
      // resumeAudioContext is idempotent and safe to call multiple times
    };

    document.addEventListener('click', resumeAudio);
    document.addEventListener('touchstart', resumeAudio);
    document.addEventListener('keydown', resumeAudio);

    return () => {
      // Cleanup the event listeners when the component unmounts.
      document.removeEventListener('click', resumeAudio);
      document.removeEventListener('touchstart', resumeAudio);
      document.removeEventListener('keydown', resumeAudio);
    };
  }, [resumeAudioContext, isReplayingEvents]);

  // Monitor window status changes and stop speech when window closes
  useEffect(() => {
    const prevStatus = prevWindowStatusRef.current;
    const currentStatus = windowStatus;

    // If window status changed from 'opened' to 'closed', skip any stale
    // "waiting on players" warning speech without disrupting other speeches
    // (e.g. kick commentary) that may be playing or queued.
    if (prevStatus === 'opened' && currentStatus === 'closed') {
      speechManager.skipSpeechByContent('player.waitingOnPlayers');
    }

    // Update the ref for next comparison
    prevWindowStatusRef.current = currentStatus;
  }, [windowStatus, speechManager]);

  // Auto-start music when audio context becomes available
  useEffect(() => {
    // Only auto-start music playlists on room/game routes
    if (!isHostRoute || !webAudioRef.current) return;

    const context = webAudioRef.current.getContext();
    if (!context) return;

    const tryStartMusic = () => {
      if (
        context.state === 'running' &&
        !musicManager.isMusicPlaying &&
        musicManager.hasMusicPlaylist &&
        musicManager.musicReadyToPlay
      ) {
        logger.debug('Audio context running, starting music playlist');
        musicManager.playMusicPlaylist();
      }
    };

    // Try starting music immediately
    tryStartMusic();

    // Listen for audio context state changes (event-driven, no polling)
    const handleStateChange = () => {
      logger.debug(`Audio context state changed to: ${context.state}`);
      tryStartMusic();
    };

    context.addEventListener('statechange', handleStateChange);
    return () => context.removeEventListener('statechange', handleStateChange);
  }, [musicManager, isHostRoute]);

  // Memoize the context value
  const contextValue: MediaContextValue = useMemo(
    () => ({
      // Speech domain
      isSpeaking: speechManager.isSpeaking,
      speechMediaIdPlaying: speechManager.speechMediaIdPlaying,
      stopSpeechPlayback: speechManager.stopSpeechPlayback,
      skipCurrentSpeech: speechManager.skipCurrentSpeech,
      playSpeechBuffer: speechManager.playSpeechBuffer,
      waitForSpeech: speechManager.waitForSpeech,
      waitForSpeechById: speechManager.waitForSpeechById,

      // SFX domain
      isSfxPlaying: sfxManager.isSfxPlaying,
      requestSoundEffect: sfxManager.requestSoundEffect,
      requestGameSfx: sfxManager.requestGameSfx,
      playSoundEffect: sfxManager.playSoundEffect,
      playNote: sfxManager.playNote,
      stopSfxPlayback: sfxManager.stopSfxPlayback,

      // Music domain
      isMusicPlaying: musicManager.isMusicPlaying,
      hasMusicPlaylist: musicManager.hasMusicPlaylist,
      musicTrackCurrent: musicManager.musicTrackCurrent,
      musicTrackCurrentMetadata: musicManager.musicTrackCurrentMetadata,
      shouldShowMusicDisplay: musicManager.shouldShowMusicDisplay,
      currentDisplayTrackId: musicManager.currentDisplayTrackId,
      createMusicPlaylist: musicManager.createMusicPlaylist,
      playMusicPlaylist: musicManager.playMusicPlaylist,
      flushMusicManager: musicManager.flushMusicManager,
      updateTrackWithSignedUrl: musicManager.updateTrackWithSignedUrl,

      // Volume domain
      isMuted: volumeManager.isMuted,
      setIsMuted: volumeManager.setIsMuted,
      toggleMute: volumeManager.toggleMute,
      duckMusic: volumeManager.duckMusic,
      unduckMusic: volumeManager.unduckMusic,
      updateVolumeSettings: volumeManager.updateVolumeSettings,
      currentVolumes: volumeManager.currentVolumes,

      // Image domain
      loadImageFromEvent: imageManager.loadImageFromEvent,
      loadImageFromUrl: imageManager.loadImageFromUrl,
      requestImageScene: imageManager.requestImageScene,
      requestGameScene: imageManager.requestGameScene,
      getImageSceneItem: imageManager.getImageSceneItem,

      // WebAudio service and context control
      webAudio: webAudioRef.current,
      webAudioReady,
      resumeAudioContext,

      // Audio element refs
      speechAudioRef,
      sfxAudioRef,
      musicAudioRef,
    }),
    [
      // Speech domain
      speechManager.isSpeaking,
      speechManager.speechMediaIdPlaying,
      speechManager.stopSpeechPlayback,
      speechManager.skipCurrentSpeech,
      speechManager.playSpeechBuffer,
      speechManager.waitForSpeech,
      speechManager.waitForSpeechById,

      // SFX domain
      sfxManager.isSfxPlaying,
      sfxManager.requestSoundEffect,
      sfxManager.requestGameSfx,
      sfxManager.playSoundEffect,
      sfxManager.playNote,
      sfxManager.stopSfxPlayback,

      // Music domain
      musicManager.isMusicPlaying,
      musicManager.hasMusicPlaylist,
      musicManager.musicTrackCurrent,
      musicManager.musicTrackCurrentMetadata,
      musicManager.shouldShowMusicDisplay,
      musicManager.currentDisplayTrackId,
      musicManager.createMusicPlaylist,
      musicManager.playMusicPlaylist,
      musicManager.updateTrackWithSignedUrl,
      musicManager.flushMusicManager,

      // Volume domain (includes mute management)
      volumeManager.isMuted,
      volumeManager.setIsMuted,
      volumeManager.toggleMute,
      volumeManager.duckMusic,
      volumeManager.unduckMusic,
      volumeManager.updateVolumeSettings,
      volumeManager.currentVolumes,

      // Image domain
      imageManager.loadImageFromEvent,
      imageManager.loadImageFromUrl,
      imageManager.requestImageScene,
      imageManager.requestGameScene,
      imageManager.getImageSceneItem,

      // WebAudio service and context control
      webAudioReady,
      resumeAudioContext,

      // Audio element refs
      speechAudioRef,
      sfxAudioRef,
      musicAudioRef,
    ]
  );

  return (
    <MediaContext.Provider value={contextValue}>
      {children}
      {/* Hidden audio elements used by audio services */}
      <audio
        id="speech-audio"
        ref={speechAudioRef}
        style={{ display: 'none' }}
      />
      <audio id="sfx-audio" ref={sfxAudioRef} style={{ display: 'none' }} />
      <audio id="music-audio" ref={musicAudioRef} style={{ display: 'none' }} />
    </MediaContext.Provider>
  );
}
